'use client';
import { isComputerRoute } from '#pplx/frontend/apps/ask/layout/sidebar/core/shared/constants.js';
import { routes } from '#pplx/frontend/apps/ask/routing/spaRoutes.js';
import { useLossAversion } from '#pplx/frontend/apps/ask/search/loss-aversion/useLossAversion.js';
import { useInputActions } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { useCallback } from 'react';
// Single-file event: defined here at its only call site rather than in a shared package.
const quickAskKeyboardShortcutEvent = EVENT_PAYLOAD;
/**
 * Shared navigation action for the "New Thread" keyboard shortcut and
 * "New Thread / New Task" **sidebar button**.
 *
 * Handles loss-aversion gating, analytics, and the sidebar button's
 * computer-route branch. The keyboard shortcut intentionally follows the
 * same search navigation behavior on every page.
 *
 * @param origin – analytic origin label (e.g. `ModalOrigin.NEW_THREAD_BUTTON`)
 */
export function useNewThreadNavigation(origin) {
    const { track } = useInstrumentation();
    const { device: { isWindowsApp }, } = useWebPlatform();
    const { shouldShowLossAversionModal, handleOpenLossAversion } = useLossAversion();
    const router = useRouter();
    const pathname = usePathname();
    const { setShouldTriggerFocus } = useInputActions();
    return useCallback(() => {
        // Computer mode: the sidebar button stays in computer context. The
        // keyboard shortcut keeps the same new-thread behavior across pages.
        if (isComputerRoute(pathname) && origin !== ModalOrigin.NEW_THREAD_SHORTCUT) {
            if (pathname === '/computer' || pathname === '/computer/new') {
                setShouldTriggerFocus(true);
            }
            else {
                router.push(routes['/computer'].href(), undefined, 'New thread navigation (computer)');
            }
            return;
        }
        // Loss aversion gate (skip on Windows native app)
        if (!isWindowsApp && shouldShowLossAversionModal) {
            handleOpenLossAversion({
                origin,
                callback: () => {
                    if (origin === ModalOrigin.NEW_THREAD_SHORTCUT) {
                        track(quickAskKeyboardShortcutEvent({
                            name: 'quick ask keyboard shortcut',
                            data: {},
                        }));
                    }
                    setShouldTriggerFocus(true);
                    router.push(routes['/'].href(), undefined, 'Quick ask keyboard shortcut (loss aversion)');
                },
            });
            return;
        }
        // Standard search: navigate or focus
        if (origin === ModalOrigin.NEW_THREAD_SHORTCUT) {
            track(quickAskKeyboardShortcutEvent({ name: 'quick ask keyboard shortcut', data: {} }));
        }
        setShouldTriggerFocus(true);
        if (pathname !== '/') {
            router.push(routes['/'].href(), undefined, 'Quick ask keyboard shortcut');
        }
    }, [
        pathname,
        isWindowsApp,
        shouldShowLossAversionModal,
        handleOpenLossAversion,
        origin,
        track,
        router,
        setShouldTriggerFocus,
    ]);
}
//# sourceMappingURL=useNewThreadNavigation.js.map